// Cognito Hosted UI sign-in via OAuth 2.0 Authorization Code + PKCE.
// Hand-rolled (no aws-amplify) to keep the bundle small for an internal tool.

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string; // e.g., "https://auth.aws.fortwana.sk"
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

const TOKEN_KEY = 'mail-sim:tokens';
const VERIFIER_KEY = 'mail-sim:pkce-verifier';
const RETURN_TO_KEY = 'mail-sim:return-to';

interface Tokens {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_at: number; // epoch ms
}

// ─── PKCE helpers ──────────────────────────────────────────────────────
function base64UrlEncode(bytes: Uint8Array): string {
  let s = btoa(String.fromCharCode(...bytes));
  return s.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function challenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

// ─── Public API ────────────────────────────────────────────────────────
export function getStoredTokens(): Tokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Tokens;
    if (t.expires_at < Date.now()) return null; // expired
    return t;
  } catch {
    return null;
  }
}

export async function startSignIn(returnTo?: string): Promise<void> {
  const verifier = randomVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  const code_challenge = await challenge(verifier);
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
    code_challenge,
    code_challenge_method: 'S256',
  });
  window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

export async function handleCallback(): Promise<{ returnTo: string | null }> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  if (!code) throw new Error('No code in callback URL');
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier (sign-in flow not started?)');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });
  const res = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  }
  const json = (await res.json()) as { id_token: string; access_token: string; refresh_token?: string; expires_in: number };
  const tokens: Tokens = {
    id_token: json.id_token,
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    expires_at: Date.now() + json.expires_in * 1000,
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  sessionStorage.removeItem(VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return { returnTo };
}

export function signOut(): void {
  localStorage.removeItem(TOKEN_KEY);
  // Also redirect through Cognito's /logout endpoint to wipe Cognito's session
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: window.location.origin,
  });
  window.location.href = `${COGNITO_DOMAIN}/logout?${params.toString()}`;
}

// JWT payload decode (no validation — server validates).
export function decodeIdToken(token: string): { email?: string; sub?: string; [k: string]: unknown } {
  try {
    const payload = token.split('.')[1];
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return {};
  }
}
